import React, { Suspense } from 'react'
import { Outlet } from 'react-router-dom'
import { Toaster } from 'react-hot-toast'
import type { RouteRecord } from 'vite-react-ssg'
import AuthProvider from './hooks/provider'

import HomePage from './pages/index'
import CodePage from './pages/code'
import NewsPage from './pages/news'
import PrivacyPage from './pages/privacy/index'
import NotFoundPage from './pages/404'
import PreviewPage from './pages/preview/index'
import ChangelogPage from './pages/changelog/index'

// Labs pages
import LabsPage from './pages/labs/index'
import UseCasesPage from './pages/labs/usecases/index'
import FrameworksPage from './pages/labs/frameworks/index'
import StateOfAgentsPage from './pages/labs/state-of-agents/index'
import AIAgentBooksPage from './pages/labs/ai-agent-books/index'
import AICostTrendsPage from './pages/labs/ai-cost-trends/index'


// i18n only bundles English; the rest is fetched, so a first paint can wait on it
const I18nLoadingFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-primary">
    <div className="flex flex-col items-center gap-3">
      <div className="w-8 h-8 border-2 border-accent border-t-transparent rounded-full animate-spin" />
      <span className="text-secondary text-sm">Loading...</span>
    </div>
  </div>
)

/**
 * What App.tsx used to render, as a layout route: the providers and the
 * Toaster wrap an <Outlet /> instead of <Routes>.
 */
const Layout: React.FC = () => (
  <AuthProvider>
    <Suspense fallback={<I18nLoadingFallback />}>
      <Outlet />
    </Suspense>
    <Toaster position="top-right" toastOptions={{ duration: 4000 }} />
  </AuthProvider>
)

/**
 * Routes as data so vite-react-ssg can walk them and emit one HTML file per
 * path. Every entry here becomes dist/<path>/index.html — which is what stops
 * GitHub Pages returning 404 for everything but "/".
 *
 * The catch-all is deliberately NOT prerendered: it has no static path, and
 * static/404.html still handles genuinely unknown URLs.
 */
export const routes: RouteRecord[] = [
  {
    path: '/',
    element: <Layout />,
    children: [
      { index: true, element: <HomePage />, entry: 'src/pages/index.tsx' },
      { path: 'preview', element: <PreviewPage /> },
      { path: 'code', element: <CodePage /> },
      { path: 'news', element: <NewsPage /> },
      // Printed inside the book - this path must not change. See dibia/RELEASING.md.
      { path: 'changelog', element: <ChangelogPage /> },
      { path: 'privacy', element: <PrivacyPage /> },
      { path: 'labs', element: <LabsPage /> },
      { path: 'labs/usecases', element: <UseCasesPage /> },
      { path: 'labs/frameworks', element: <FrameworksPage /> },
      { path: 'labs/state-of-agents', element: <StateOfAgentsPage /> },
      { path: 'labs/ai-agent-books', element: <AIAgentBooksPage /> },
      { path: 'labs/ai-cost-trends', element: <AICostTrendsPage /> },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
]

export default routes
